import React, { useState, useEffect, useRef } from 'react'
import Card from '../../components/Card.jsx'
import * as api from '../../services/api.js'

const DAYS = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday']
const MEALS = ['Breakfast', 'Lunch', 'Dinner', 'Snack']

// A slot value is either '' | string (free text) | { text, recipeId }
function slotText(val) {
  if (!val) return ''
  return typeof val === 'object' ? val.text : val
}
function slotRecipeId(val) {
  return val && typeof val === 'object' ? val.recipeId : null
}
function isLinked(val) {
  return val && typeof val === 'object' && val.recipeId != null
}

function MealSlot({ label, slotKey, value, recipes, onChange }) {
  const [pickerOpen, setPickerOpen] = useState(false)
  const wrapRef = useRef()
  const linked = isLinked(value)

  useEffect(() => {
    function handleClick(e) {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) {
        setPickerOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClick)
    return () => document.removeEventListener('mousedown', handleClick)
  }, [])

  function selectRecipe(recipe) {
    onChange(slotKey, { text: recipe.name, recipeId: recipe.id })
    setPickerOpen(false)
  }

  function unlink() {
    onChange(slotKey, slotText(value))
  }

  function handleText(e) {
    onChange(slotKey, e.target.value)
  }

  return (
    <div className="meal-plan-slot" ref={wrapRef}>
      <span className="meal-plan-meal-label">{label}</span>
      <div className="meal-slot-input-row">
        <input
          className={`meal-plan-input${linked ? ' linked' : ''}`}
          value={slotText(value)}
          onChange={handleText}
          readOnly={linked}
          placeholder="—"
        />
        {linked ? (
          <button className="meal-slot-unlink-btn" title="Unlink recipe" onClick={unlink}>✕</button>
        ) : (
          <button
            className="meal-slot-pick-btn"
            title="Pick from recipes"
            onClick={() => setPickerOpen(v => !v)}
          >
            ≡
          </button>
        )}
        {pickerOpen && (
          <div className="recipe-picker-dropdown">
            {recipes.length === 0 ? (
              <span className="recipe-picker-empty">No saved recipes yet</span>
            ) : (
              recipes.map(r => (
                <button
                  key={r.id}
                  className="recipe-picker-item"
                  onClick={() => selectRecipe(r)}
                >
                  {r.name}
                </button>
              ))
            )}
          </div>
        )}
      </div>
    </div>
  )
}

function DayColumn({ day, prefix, plan, recipes, onChange }) {
  return (
    <div className="meal-plan-day">
      <div className="meal-plan-day-label">{day.slice(0, 3)}</div>
      {MEALS.map(meal => (
        <MealSlot
          key={meal}
          label={meal}
          slotKey={`${prefix}${day}-${meal}`}
          value={plan[`${prefix}${day}-${meal}`] || ''}
          recipes={recipes}
          onChange={onChange}
        />
      ))}
    </div>
  )
}

export default function CookingMealPlan({ recipes = [], onAddToShoppingList }) {
  const [view, setView]         = useState('week')
  const [weekPlan, setWeekPlan]   = useState({})
  const [monthPlan, setMonthPlan] = useState({})

  useEffect(() => {
    api.getMealPlan('week').then(d => setWeekPlan(d || {}))
    api.getMealPlan('month').then(d => setMonthPlan(d || {}))
  }, [])

  const plan    = view === 'week' ? weekPlan    : monthPlan
  const setPlan = view === 'week' ? setWeekPlan : setMonthPlan

  function setSlot(key, value) {
    setPlan(p => {
      const next = { ...p, [key]: value }
      api.saveMealPlan(view, next)
      return next
    })
  }

  function clearAll() {
    setPlan({})
    api.saveMealPlan(view, {})
  }

  function collectIngredients() {
    const items = []
    const seen  = new Set()

    Object.values(plan).forEach(slot => {
      if (!slot) return

      if (isLinked(slot)) {
        const recipe = recipes.find(r => r.id === slot.recipeId)
        if (recipe && recipe.ingredients?.length) {
          recipe.ingredients.filter(i => i.name?.trim()).forEach(ing => {
            const key = ing.name.trim().toLowerCase()
            if (!seen.has(key)) {
              seen.add(key)
              items.push({ name: ing.name.trim(), amount: ing.amount || '', category: 'Other' })
            }
          })
          return
        }
      }

      // Free text — just add the meal name as a single item
      const text = slotText(slot).trim()
      if (text && !seen.has(text.toLowerCase())) {
        seen.add(text.toLowerCase())
        items.push({ name: text, amount: '', category: 'Other' })
      }
    })

    return items
  }

  const hasEntries = Object.values(plan).some(v => slotText(v).trim())

  return (
    <Card label="Cooking" title="Meal Plan">
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1.5rem', flexWrap: 'wrap', gap: '0.5rem' }}>
        <div className="view-toggle">
          <button className={`view-btn ${view === 'week' ? 'active' : ''}`} onClick={() => setView('week')}>This Week</button>
          <button className={`view-btn ${view === 'month' ? 'active' : ''}`} onClick={() => setView('month')}>Month Plan</button>
        </div>
        <div style={{ display: 'flex', gap: '8px' }}>
          {hasEntries && onAddToShoppingList && (
            <button className="ghost-btn" onClick={() => onAddToShoppingList(collectIngredients())}>
              Add to shopping list
            </button>
          )}
          <button className="ghost-btn" onClick={clearAll}>Clear all</button>
        </div>
      </div>

      <p style={{ fontSize: '0.78rem', color: 'var(--stone)', marginBottom: '1rem', fontStyle: 'italic' }}>
        Type a meal name, or press <strong style={{ fontStyle: 'normal' }}>≡</strong> to pick from your saved recipes — linked recipes add their full ingredient list to the shopping list.
      </p>

      {view === 'week' && (
        <div className="meal-plan-grid">
          {DAYS.map(day => (
            <DayColumn
              key={day}
              day={day}
              prefix=""
              plan={plan}
              recipes={recipes}
              onChange={setSlot}
            />
          ))}
        </div>
      )}

      {view === 'month' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '1.25rem' }}>
          {[1, 2, 3, 4].map(week => (
            <div key={week}>
              <p className="field-label" style={{ marginBottom: '8px' }}>Week {week}</p>
              <div className="meal-plan-grid">
                {DAYS.map(day => (
                  <DayColumn
                    key={day}
                    day={day}
                    prefix={`W${week}-`}
                    plan={plan}
                    recipes={recipes}
                    onChange={setSlot}
                  />
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </Card>
  )
}
